// 统一的 API 客户端 — 所有 fetch 走这里，类型安全 + 错误处理统一
import { PropertiesResponseSchema, PropertySchema, type Property } from './types';

const API_BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(`${API_BASE}${path}`, {
		...init,
		headers: {
			'Content-Type': 'application/json',
			...(init?.headers ?? {}),
		},
	});
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`API ${res.status}: ${text || res.statusText}`);
	}
	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}

async function requestForm<T>(path: string, form: FormData): Promise<T> {
	const res = await fetch(`${API_BASE}${path}`, {
		method: 'PUT',
		body: form,
	});
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`API ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as T;
}

export interface ListPropertiesParams {
	page?: number;
	pageSize?: number;
	search?: string;
	status?: string;
	isActive?: string;
}

export const api = {
	async listProperties(params: ListPropertiesParams = {}) {
		const qs = new URLSearchParams();
		qs.set('page', String(params.page ?? 1));
		qs.set('pageSize', String(params.pageSize ?? 20));
		if (params.search) qs.set('search', params.search);
		if (params.status) qs.set('status', params.status);
		if (params.isActive) qs.set('isActive', params.isActive);
		const raw = await request<unknown>(`/properties?${qs}`);
		const parsed = PropertiesResponseSchema.parse(raw);
		// 后端不返回 totalPages，前端补
		const pageSize = params.pageSize ?? 20;
		const page = params.page ?? 1;
		return {
			...parsed,
			page: parsed.page ?? page,
			pageSize: parsed.pageSize ?? pageSize,
			totalPages: parsed.totalPages ?? Math.max(1, Math.ceil(parsed.total / pageSize)),
		};
	},

	async getProperty(id: number) {
		const raw = await request<unknown>(`/properties/${id}`);
		return PropertySchema.parse(raw);
	},

	async createProperty(data: Partial<Property>) {
		return request<Property | number>(`/properties`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	},

	async updateProperty(id: number, data: Partial<Property>) {
		return request<Property>(`/properties/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	},

	async deleteProperty(id: number) {
		return request<void>(`/properties/${id}`, { method: 'DELETE' });
	},

	async batchDelete(ids: number[]) {
		return request<{ count: number }>(`/properties/batch-delete`, {
			method: 'POST',
			body: JSON.stringify({ ids }),
		});
	},

	getPropertyVideoUrl(id: number) {
		return `${API_BASE}/properties/${id}/video`;
	},

	async uploadPropertyVideo(id: number, file: File) {
		const form = new FormData();
		form.set('video', file);
		return requestForm<Property>(`/properties/${id}/video`, form);
	},

	async deletePropertyVideo(id: number) {
		return request<Property>(`/properties/${id}/video`, { method: 'DELETE' });
	},

	async getStats() {
		return request<{ total: number; sale: number; rent: number; sold: number }>(`/stats`);
	},

	async agent(messages: AgentMessage[], signal?: AbortSignal): Promise<Response> {
		const res = await fetch(`${API_BASE}/agent`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ messages }),
			signal,
		});
		if (!res.ok) {
			const text = await res.text().catch(() => '');
			throw new Error(`Agent ${res.status}: ${text}`);
		}
		return res;
	},
};

// AI Agent 消息类型
export type AgentContent =
	| string
	| Array<
			| { type: 'text'; text: string }
			| { type: 'image_url'; image_url: { url: string } }
	  >;
export interface AgentMessage {
	role: 'user' | 'assistant';
	content: AgentContent;
}
